import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Delete, DragHandle, ExpandMore } from '@mui/icons-material'
import { AccordionSummary, Box, Checkbox, CircularProgress, IconButton, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { useDeleteMiscItemMutation, useGetMiscItemsQuery } from '@/app/services/miscItems'
import { useGetQuoteQuery } from '@/app/services/quotes'
import { selectLocale } from '@/app/slices/appSlice'
import {
    addSelectedMiscItem,
    removeExpandedMiscItem,
    removeSelectedMiscItem,
    selectExpandedMiscItems,
    selectSelectedMiscItems,
} from '@/app/slices/miscItemsSlice'
import { selectCurrencyCode, selectOrganisationId, selectUseImperialUnits } from '@/app/slices/organisationSlice'
import { selectedQuoteStatus, selectIsCalculatingQuote, setSelectedQuoteStatus } from '@/app/slices/quoteItemsSlice'
import TbxDialog from '@/common/components/TbxDialog/TbxDialog'
import { useCurrencyFormatter } from '@/common/hooks'
import useNumberFormatter from '@/common/hooks/useNumberFormatter'
import MiscItemIcon from '@/common/icons/MiscItemIcon/MiscItemIcon'
import { QUOTE_STATUS_ARRAY } from '@/common/utils'
import DocumentsButton from '@/features/quotes/components/QuoteAttachments/DocumentsButton'
import DocumentsDialog from '@/features/quotes/components/QuoteAttachments/DocumentsDialog'
import MiscItemAttachmentsPanel from '@/features/quotes/components/QuoteAttachments/MiscItemAttachmentsPanel'

import MiscItemName from './MiscItemName'

const classes = {
    summaryRoot: {
        height: 62,
        backgroundColor: 'accordionSummary.main',
        '& .MuiAccordionSummary-content': {
            justifyContent: 'space-between',
            margin: '12px 0',
            '&.Mui-expanded': {
                margin: '12px 0',
            },
        },
    },
    AccordionSummaryLeft: {
        gap: 1.5,
    },

    AccordionSummaryRight: {
        gap: 6,
    },

    summaryThumbnailWrapper: {
        height: '50px',
        width: '50px',
    },

    summaryThumbnailImg: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        objectPosition: 'center',
    },

    miscItemSummaryCollapsed: {
        gap: 5,
    },
    miscItemSummaryCollapsed_LinePrice: {
        width: '70px',
    },
    smallText: {
        fontSize: '0.75rem',
    },

    iconColor: { color: 'text.secondary' },
}

/**
 * MiscItemSummary - The header row of a misc item: index, drag handle, selection, name, collapsed
 * figures, and the documents and delete controls.
 *
 * @param {Object} props
 * @param {Object} [props.listeners] - drag listeners for the handle
 * @param {string} props.miscItemId
 * @param {Function} [props.setActivatorNodeRef] - ref setter for the drag handle
 * @param {boolean} [props.showAttachments] - Shows the item's documents control; read once by the quote screen
 */
const MiscItemSummary = ({ listeners, miscItemId, setActivatorNodeRef, showAttachments = false }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const { quoteId } = useParams()

    const organisationId = useSelector(selectOrganisationId)
    const useImperialUnits = useSelector(selectUseImperialUnits)
    const locale = useSelector(selectLocale)
    const currencyCode = useSelector(selectCurrencyCode)

    const { formatCurrency } = useCurrencyFormatter({
        currencyCode,
        locale,
    })

    const hidePrices = useSelector(selectedQuoteStatus) === QUOTE_STATUS_ARRAY.NotCalculated

    const selectedMiscItemsArray = useSelector(selectSelectedMiscItems)
    const expandedMiscItemsArray = useSelector(selectExpandedMiscItems)
    const isCalculating = useSelector(selectIsCalculatingQuote)

    const { n } = useNumberFormatter()

    const { n: q } = useNumberFormatter({ numberOfDecimalPlaces: 0 })

    const formatNumberWithX = (str) => {
        const parts = str.split('x')
        const formattedParts = parts.map((part) => n(Number(part)))
        return formattedParts.join(' x ')
    }

    const { data: selectedQuote, isFetching: isFetchingQuote } = useGetQuoteQuery({
        organisationId,
        quoteId,
    })

    const { miscItem } = useGetMiscItemsQuery(
        {
            organisationId,
            quoteId,
        },
        {
            selectFromResult: ({ data }) => ({
                miscItem: data?.find((item) => item.id === miscItemId),
            }),
        }
    )

    const [deleteMiscItem, { isLoading: isDeleting }] = useDeleteMiscItemMutation()

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
    const [isDocumentsDialogOpen, setIsDocumentsDialogOpen] = useState(false)

    // Derived values from Redux state (not local state synchronized via useEffect)
    const isSelected = selectedMiscItemsArray.includes(miscItem?.id)
    const isExpanded = expandedMiscItemsArray.includes(miscItem?.id)

    const unit = useImperialUnits ? 'in' : 'mm'
    const weightUnits = useImperialUnits ? 'lb' : 'kg'

    const handleDeleteDialogOpen = (event) => {
        event.stopPropagation()
        setIsDeleteDialogOpen(true)
    }

    const handleDeleteDialogClose = () => {
        setIsDeleteDialogOpen(false)
    }

    const handleRemoveQuoteItem = async () => {
        if (selectedQuote.status !== QUOTE_STATUS_ARRAY.NotCalculated) {
            dispatch(setSelectedQuoteStatus(QUOTE_STATUS_ARRAY.NotCalculated))
        }
        try {
            await deleteMiscItem({
                organisationId,
                quoteId,
                miscItemId: miscItem?.id,
            }).unwrap()
            dispatch(removeExpandedMiscItem(miscItem?.id))
            enqueueSnackbar(`${miscItem?.name} ${t('deleted')}`, {
                variant: 'success',
            })
            setIsDeleteDialogOpen(false)
        } catch {
            enqueueSnackbar(`${t('Failed to delete')} ${miscItem?.name} from quote`, {
                variant: 'error',
            })
            setIsDeleteDialogOpen(false)
        }
    }

    const handleSelectionChange = () => {
        if (isSelected) {
            dispatch(removeSelectedMiscItem(miscItem?.id))
        } else {
            dispatch(addSelectedMiscItem(miscItem?.id))
        }
    }

    return (
        <>
            <AccordionSummary
                aria-controls={`panel${miscItem?.id}-content`}
                aria-label="Expand"
                expandIcon={<ExpandMore />}
                id={`panel${miscItem?.id}-header`}
                sx={classes.summaryRoot}
            >
                <Box
                    sx={[
                        {
                            alignItems: 'center',
                            display: 'flex',
                        },
                        classes.AccordionSummaryLeft,
                    ]}
                >
                    <Typography variant="body1">{miscItem?.index + 1}</Typography>
                    <IconButton
                        aria-label="Drag"
                        color="secondary"
                        size="small"
                        {...listeners}
                        ref={setActivatorNodeRef}
                        sx={{ cursor: 'grab' }}
                    >
                        <DragHandle />
                    </IconButton>
                    <Checkbox
                        checked={isSelected}
                        color="primary"
                        inputProps={{
                            'data-testid': 'select-misc-item',
                        }}
                        size="small"
                        sx={{ p: 0 }}
                        onChange={handleSelectionChange}
                        onClick={(event) => event.stopPropagation()}
                    />
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 2,
                        }}
                    >
                        {!isExpanded ? <MiscItemIcon titleAccess={t('Miscellaneous item icon')} /> : null}
                        <MiscItemName
                            id={miscItem?.id}
                            name={miscItem?.name}
                        />
                    </Box>
                </Box>
                <Box
                    sx={[
                        {
                            alignItems: 'center',
                            display: 'flex',
                        },
                        classes.AccordionSummaryRight,
                    ]}
                >
                    {isCalculating || isFetchingQuote ? (
                        <Box
                            data-testid="calculating-header"
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                            translate="no"
                        >
                            <Typography
                                color="secondary"
                                sx={{
                                    letterSpacing: 1,
                                    mr: 2,
                                    textTransform: 'uppercase',
                                }}
                                variant="strong2"
                            >
                                {isCalculating ? t('Calculating') : t('Loading')}
                            </Typography>
                            <CircularProgress
                                color="secondary"
                                size={24}
                            />
                        </Box>
                    ) : null}

                    {!isExpanded && !isCalculating && !isFetchingQuote ? (
                        <Box
                            sx={[
                                {
                                    display: 'flex',
                                },
                                classes.miscItemSummaryCollapsed,
                            ]}
                        >
                            {miscItem?.dimensions ? (
                                <Box
                                    className="miscItemSummaryCollapsed_Quantity"
                                    sx={{
                                        alignItems: 'flex-end',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Typography
                                        sx={classes.smallText}
                                        variant="body2"
                                    >
                                        {t('Dimensions')}
                                    </Typography>
                                    <Typography variant="body2">
                                        {formatNumberWithX(miscItem?.dimensions)} {unit}
                                    </Typography>
                                </Box>
                            ) : null}

                            {miscItem?.weight ? (
                                <Box
                                    className="miscItemSummaryCollapsed_Quantity"
                                    sx={{
                                        alignItems: 'flex-end',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Typography
                                        sx={classes.smallText}
                                        variant="body2"
                                    >
                                        {t('Weight')}
                                    </Typography>
                                    <Typography variant="body2">
                                        {n(miscItem?.weight)} {weightUnits}
                                    </Typography>
                                </Box>
                            ) : null}

                            {miscItem?.productionTimePerItemSeconds ? (
                                <Box
                                    className="miscItemSummaryCollapsed_Quantity"
                                    sx={{
                                        alignItems: 'flex-end',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Typography
                                        sx={classes.smallText}
                                        variant="body2"
                                    >
                                        {t('Time')}
                                    </Typography>
                                    <Typography variant="body2">
                                        {n(miscItem?.productionTimePerItemSeconds)} {t('s')}
                                    </Typography>
                                </Box>
                            ) : null}

                            <Box
                                className="miscItemSummaryCollapsed_Quantity"
                                sx={{
                                    alignItems: 'flex-end',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Typography
                                    sx={classes.smallText}
                                    variant="body2"
                                >
                                    {t('Quantity')}
                                </Typography>
                                <Typography variant="body2">x {q(miscItem?.quantity)}</Typography>
                            </Box>
                            <Box
                                className="miscItemSummaryCollapsed_UnitPrice"
                                sx={{
                                    alignItems: 'flex-end',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Typography
                                    sx={classes.smallText}
                                    variant="body2"
                                >
                                    {t('Unit price')}
                                </Typography>

                                {isCalculating ? (
                                    <CircularProgress
                                        size={14}
                                        sx={classes.calculatingIcon}
                                    />
                                ) : null}

                                {!hidePrices && !isCalculating ? (
                                    <Typography variant="body2">
                                        {miscItem?.itemPrice
                                            ? formatCurrency(miscItem?.itemPrice)
                                            : String.fromCharCode(8212)}
                                    </Typography>
                                ) : (
                                    <Typography
                                        style={{ fontWeight: 700 }}
                                        variant="body2"
                                    >
                                        {String.fromCharCode(8212)}
                                    </Typography>
                                )}
                            </Box>
                            <Box
                                alignItems="flex-end"
                                display="flex"
                                flexDirection="column"
                                sx={classes.miscItemSummaryCollapsed_LinePrice}
                            >
                                <Typography
                                    sx={classes.smallText}
                                    variant="body2"
                                >
                                    {t('Line price')}
                                </Typography>

                                {isCalculating ? (
                                    <CircularProgress
                                        size={14}
                                        sx={classes.calculatingIcon}
                                    />
                                ) : null}

                                {!hidePrices && !isCalculating ? (
                                    <Typography
                                        style={{ fontWeight: 700 }}
                                        variant="body2"
                                    >
                                        {miscItem?.linePrice
                                            ? formatCurrency(miscItem?.linePrice)
                                            : String.fromCharCode(8212)}
                                    </Typography>
                                ) : (
                                    <Typography
                                        style={{ fontWeight: 700 }}
                                        variant="body2"
                                    >
                                        {String.fromCharCode(8212)}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ) : null}

                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                        }}
                    >
                        {showAttachments ? (
                            <DocumentsButton
                                attachments={miscItem?.attachments}
                                onClick={() => setIsDocumentsDialogOpen(true)}
                            />
                        ) : null}

                        <IconButton
                            aria-label="delete"
                            color="secondary"
                            data-testid="delete-quote-item-name"
                            onClick={(event) => handleDeleteDialogOpen(event)}
                            onFocus={(event) => event.stopPropagation()}
                        >
                            <Delete />
                        </IconButton>
                    </Box>
                </Box>
            </AccordionSummary>
            <TbxDialog
                closeButtonText={t('cancel')}
                confirmButtonText={isDeleting ? t('Deleting...') : t('Yes, delete')}
                content={`${t('Are you sure you want to delete part')} ${miscItem?.name}`}
                handleClose={handleDeleteDialogClose}
                handleConfirmClose={handleRemoveQuoteItem}
                isLoading={isDeleting}
                isOpen={isDeleteDialogOpen}
                key={miscItemId}
                title={`${t('Delete part')} ${miscItem?.name}?`}
            />
            {showAttachments ? (
                <DocumentsDialog
                    itemName={miscItem?.name}
                    open={isDocumentsDialogOpen}
                    subtitle={t('Attachments for this item only')}
                    onClose={() => setIsDocumentsDialogOpen(false)}
                >
                    <MiscItemAttachmentsPanel
                        miscItemId={miscItemId}
                        organisationId={organisationId}
                        quoteId={quoteId}
                    />
                </DocumentsDialog>
            ) : null}
        </>
    )
}

MiscItemSummary.propTypes = {
    listeners: PropTypes.object,
    miscItemId: PropTypes.string,
    setActivatorNodeRef: PropTypes.func,
    showAttachments: PropTypes.bool,
}

export default MiscItemSummary
